/**
 * Turns the catalog response, the fan's pin list, and the two request states
 * into the menu the overlay renders: a pinned section in the fan's order,
 * then every sport as a heading with its REMAINING leagues under it.
 *
 * ISSUE-907 defect A: `sports` used to carry every league in the catalog,
 * pinned ones included, while `pinned` carried the same leagues again. A fan
 * with eight pins therefore read eight league names twice in one sheet, and
 * the eight extra rows pushed the sport headings below the fold of an 85dvh
 * bottom sheet. A league now appears in exactly one of the two arrays: pinned
 * leagues in `pinned`, everything else under its sport.
 *
 * Which array a league lands in is the ONLY thing that changed. Both arrays
 * render the same row (checkbox, name, drill-down), so unpinning is still
 * reachable from the pinned section and the fan never loses a control by
 * pinning. See `LeagueMenuDialog.tsx`.
 *
 * `phase` is still decided by the RAW catalog above the filter, not by the
 * filtered `sports`. A fan who has pinned every league in the catalog leaves
 * `sports` empty, and reading emptiness off the filtered array would show
 * that fan the "no leagues yet" state over a catalog that is fully stocked.
 */
import type {
  typeLeagueMenuSport,
  typeLeagueMenuView,
  typeLeagueSelectorOption,
} from "./leagueSelectorContract";
import type { typeCatalogSportInput } from "./leagueSelectorRowModel";

export function buildLeagueMenuView(input: {
  sports: typeCatalogSportInput[];
  pinnedLeagueIds: string[];
  isLoading: boolean;
  isError: boolean;
}): typeLeagueMenuView {
  const phase = input.isError
    ? "error"
    : input.isLoading
      ? "loading"
      : input.sports.length === 0
        ? "empty"
        : "ready";

  const optionsByLeagueId = new Map<string, typeLeagueSelectorOption>();
  for (const sport of input.sports) {
    for (const league of sport.leagues) {
      optionsByLeagueId.set(league.leagueId, {
        leagueId: league.leagueId,
        leagueKey: league.leagueKey,
        name: league.name,
        shortName: league.shortName,
        isComingSoon: league.visibilityState === "comingSoon",
      });
    }
  }

  const pinned: typeLeagueSelectorOption[] = [];
  for (const leagueId of input.pinnedLeagueIds) {
    const option = optionsByLeagueId.get(leagueId);
    if (option === undefined) continue;
    pinned.push(option);
  }

  // A Set rather than `Array.includes` inside the loop: the catalog is small
  // today, but this runs once per league per render of an open sheet.
  const pinnedIdSet = new Set(input.pinnedLeagueIds);

  // A sport whose every league is pinned is dropped rather than rendered as a
  // heading over nothing. `isPinned` is still computed from the pin list
  // instead of hardcoded false, so the flag stays true to its name if a later
  // edit narrows or removes the filter.
  const sports: typeLeagueMenuSport[] = input.sports
    .map((sport) => ({
      sportId: sport.sportId,
      sportKey: sport.sportKey,
      heading: sport.name,
      leagues: sport.leagues
        .filter((league) => !pinnedIdSet.has(league.leagueId))
        .map((league) => ({
          leagueId: league.leagueId,
          leagueKey: league.leagueKey,
          name: league.name,
          shortName: league.shortName,
          isComingSoon: league.visibilityState === "comingSoon",
          isPinned: input.pinnedLeagueIds.includes(league.leagueId),
        })),
    }))
    .filter((sport) => sport.leagues.length > 0);

  return { phase, pinned, sports };
}
